import { useEffect, useId, useRef, useState } from 'react'
import { activityApi, type LedgerItem, type LedgerWorthItem, type LedgerWorthTier } from '../../services/api'
import { Card, CardHeader, Chip, Hint, Meta, Section, TileGrid } from '../../ds'
import { isGoldChip } from '../decision/StoryDrawer'
import { ledgerWhy, rowStatus, windowFrom, type AttributionWindow } from './ledgerLines'

/**
 * "Worth a look" (MAR-388) — up to three decisions above the Ledger's list, each with the reason
 * it was picked, each opening its own drawer. The pick is the SERVER's, over the whole window
 * (`GET /activity?view=worth_a_look&from=`, docs/reference/api/governance.md): failed > asked you
 * > a limit stopped her > chose not to > claimed orders, newest first within a tier. Never ranked
 * from the list's page of rows — a page cannot know what lies further back in the window.
 *
 * Nothing qualifies → nothing renders (an empty block would be a claim that nothing happened).
 * A failed read says so in one quiet line rather than disappearing, so "she couldn't read it"
 * never looks like "nothing worth a look". `partial` (the window held more than the server reads)
 * says the oldest part was not considered.
 *
 * ⚠️ Scoped at the READ, like everything on Ledger: the stored answer names the workspace and
 * window it was read under and renders only when both match.
 *
 * Laid out as the v6 design draws it (MAR-402): "Worth a look" with "The rest, if you want it,
 * is below." beside it, then up to three cards three across (one per row when narrow). Each card
 * is ONE target — the whole card opens the pick's drawer — and reads, top to bottom: the line's
 * own status chip (the same chip "The night" gives it) with its time on the right, the headline,
 * and one quiet line — her why (gated exactly as the list's), or, when she gave none, the
 * server's reason for the pick ("Something broke — she couldn't finish this one."). A pick whose
 * line states no status carries the server's reason as its chip instead, so every card says why
 * it is here. No copy is composed here: the tier and the reason are the server's.
 */

type WorthState = {
  workspaceId: string
  period: AttributionWindow
  status: 'ready' | 'error'
  items: LedgerWorthItem[]
  partial: boolean
}

/** The tone of the reason chip, when the reason stands in for a line with no status of its own. */
const TIER_TONE: Record<LedgerWorthTier, 'gold' | 'sage' | 'neutral' | 'danger'> = {
  failed: 'danger',
  asked: 'gold',
  blocked: 'neutral',
  chose_not: 'sage',
  claimed: 'sage',
}

/** The server's reason as a chip: its own words, without the sentence's full stop. */
function reasonChip(reason: string): string {
  return reason.trim().replace(/\.$/, '')
}

function fmtTime(ts: string, timeZone?: string): string | null {
  const d = new Date(ts)
  if (isNaN(d.getTime())) return null
  return d.toLocaleTimeString('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hour12: false })
}

export default function WorthALook({
  workspaceId,
  period,
  onOpen,
  timeZone,
}: {
  workspaceId: string | undefined
  period: AttributionWindow
  onOpen: (item: LedgerItem) => void
  timeZone?: string
}) {
  const [state, setState] = useState<WorthState | null>(null)
  const seq = useRef(0)

  useEffect(() => {
    if (!workspaceId) return
    const mine = ++seq.current
    activityApi
      .worthALook(workspaceId, { from: windowFrom(period, new Date()) })
      .catch(() => null)
      .then((res) => {
        if (mine !== seq.current) return
        if (res?.success && res.data && Array.isArray(res.data.items)) {
          setState({ workspaceId, period, status: 'ready', items: res.data.items, partial: !!res.data.partial })
        } else {
          setState({ workspaceId, period, status: 'error', items: [], partial: false })
        }
      })
  }, [workspaceId, period])

  const view = state && state.workspaceId === workspaceId && state.period === period ? state : null
  if (!view) return null
  if (view.status === 'error') {
    return (
      <Section title="Worth a look">
        <Hint>She couldn’t pick out what’s worth a look just now — everything is still in the list below.</Hint>
      </Section>
    )
  }
  if (view.items.length === 0) return null

  return (
    <Section
      title="Worth a look"
      aside={<Hint as="span">The rest, if you want it, is below.</Hint>}
    >
      <TileGrid columns={3} data-testid="worth-grid">
        {view.items.map((item, i) => (
          <WorthCard key={item.id || `pick-${i}`} item={item} onOpen={onOpen} timeZone={timeZone} />
        ))}
      </TileGrid>
      {view.partial && (
        <Hint>She read the newest part of this window — anything older wasn’t considered.</Hint>
      )}
    </Section>
  )
}

function WorthCard({
  item,
  onOpen,
  timeZone,
}: {
  item: LedgerWorthItem
  onOpen: (item: LedgerItem) => void
  timeZone?: string
}) {
  // The card's name is its headline; the chip, the time and the quiet line describe it — so a
  // screen reader announces "Didn't greet a visitor…", not every word on the card as one name.
  const uid = useId()
  const ids = { headline: `${uid}-h`, chip: `${uid}-c`, time: `${uid}-t`, line: `${uid}-l` }
  const time = fmtTime(item.timestamp, timeZone)
  const status = rowStatus(item, isGoldChip)
  const why = ledgerWhy(item)
  const reason = item.worth?.reason?.trim() || null
  // The chip: the line's own status; failing that, the server's reason for the pick.
  const chipFromReason = !status && !!reason
  const chip = status ? (
    <Chip id={ids.chip} tone={status.tone} dot data-testid="worth-chip">
      {status.label}
    </Chip>
  ) : reason ? (
    <Chip id={ids.chip} tone={TIER_TONE[item.worth.tier] ?? 'neutral'} dot data-testid="worth-chip">
      {reasonChip(reason)}
    </Chip>
  ) : null
  // The quiet line: her why; failing that, the server's reason (unless the chip already says it).
  const line = why ? (
    <span id={ids.line} data-testid="worth-why">
      {why}
    </span>
  ) : reason && !chipFromReason ? (
    <span id={ids.line} data-testid="worth-reason">
      {reason}
    </span>
  ) : undefined
  const head = (
    <>
      <CardHeader
        inline
        icon={chip ?? undefined}
        aside={
          time ? (
            <Meta id={ids.time} dateTime={item.timestamp}>
              {time}
            </Meta>
          ) : undefined
        }
      />
      <CardHeader
        inline
        title={
          <span id={ids.headline} data-testid="worth-headline">
            {item.headline}
          </span>
        }
        description={line}
      />
    </>
  )
  const describedBy = [chip && ids.chip, time && ids.time, line && ids.line].filter(Boolean).join(' ') || undefined
  // An unreadable pick with no id is a card, not a door.
  if (!item.id) {
    return (
      <Card data-testid="worth-row" data-reason={reason ?? undefined}>
        {head}
      </Card>
    )
  }
  return (
    <Card
      interactive
      onClick={() => onOpen(item)}
      aria-labelledby={ids.headline}
      aria-describedby={describedBy}
      data-testid="worth-row"
      data-id={item.id}
      data-reason={reason ?? undefined}
    >
      {head}
    </Card>
  )
}
